import { message as EeveeMessage } from '@fuxi/eevee-ui';
import {
  MobileCode,
  ProjectRole,
  ProjectDetail,
  AccountType,
  TaskReimbursementDetail,
  Schedule,
  AccountAmountInfo,
  LoginInterface,
  CommonListResponse,
  PlatformUserConfig,
  EmailAuthCodeScopeEnum,
  ProjectsRolePermission,
  SupplementUserInfoPayload,
  UserPaymentInfo,
  PersonalPayInfo,
} from 'common/types';
import { UserRoleType } from 'common/constants';
import { getNormalParams, FastApiAxiosInstance } from 'common/service/axiosInstances';
import { omit } from 'lodash-es';

type IdCardFileType = {
  [key: string]: File | Blob;
};

const userSetting = {
  getUserImToken: async () => {
    const res = await FastApiAxiosInstance.get(`/api/v1/message/im/getUserToken`);
    return res;
  },
  createBidGroupChat: async (data: { parentTaskId: number | string; targetBidderId: number | string }) => {
    return FastApiAxiosInstance.post(`/api/v1/message/im/createBidGroupChat`, { ...data });
  },
  getCurrentUserInfo: async () => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/getCurrentUserInfo`);
    return res;
  },

  getReimbursements: (userId: number) => {
    return FastApiAxiosInstance.get<TaskReimbursementDetail[]>('/api/v1/user/reimbursementInformationList', {
      params: { userId },
    });
  },
  checkAllocatorBalance: async (params: {
    userId: number;
    credit: number | string;
    parentTaskId?: string;
    biddenId?: number;
  }): Promise<ExtendAxioResponse> => {
    const { userId, credit, parentTaskId, biddenId } = params;
    const { data } = await FastApiAxiosInstance.get(`/api/v1/user/getUserBalance`, {
      params: { userId, credit, parentTaskId, biddenId },
    });
    return data;
  },

  getProjects: async (role?: ProjectRole) => {
    // 暂无分页、请求全量的数据
    const params = { ...getNormalParams('getProjects'), _num: -1, role };
    const {
      data: { items },
    } = await FastApiAxiosInstance.get<{
      items: ProjectDetail[];
      lastIndex?: number;
      pageIndex: number;
      pageSize: number;
      total: number;
    }>('/api/v1/user/project/getProjects', {
      params,
    });
    return items || [];
  },
  // 发布需求时，获取所在项目组列表
  getPublishTaskProjects: (role?: ProjectRole) => {
    const params = { ...getNormalParams('getPublishTaskProjects'), _num: -1, role };
    return FastApiAxiosInstance.get<CommonListResponse<ProjectDetail>>('/api/v1/user/project/getProjects', {
      params,
    });
  },
  getProjectRoleOptions: async (projectCode) => {
    const params = { ...getNormalParams('getProjectRoleOptions'), projectCode };
    const {
      data: { items },
    } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/user/project/getProjectRoleOptions', {
      params,
    });
    return items;
  },
  getProjectUsers: (
    requestSource: 'CONFIG' | 'REQUIREMENT',
    projectCode: string | undefined,
    _num: number,
    _page?: number,
    _query?: string,
    signal?: AbortSignal,
  ) => {
    const params = { projectCode, _num, _page, _query, requestSource };
    if (signal) {
      params.signal = signal;
    }
    return FastApiAxiosInstance.get<{
      items: AccountType[];
      lastIndex?: number;
      pageIndex?: number;
      pageSize: number;
      total: number;
    }>('/api/v2/user/getProjectUsers', { params });
  },
  getRbacUserByEmail: async (email) => {
    const params = { ...getNormalParams('getRbacUserByEmail'), email };
    return FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/user/getRbacUserByEmail', { params });
  },
  getRbacUserByPhone: async (phone) => {
    const params = { ...getNormalParams('getRbacUserByPhone'), phone };
    return FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/user/getRbacUserByPhone', { params });
  },
  getUserRoleInfo: async (name, projectCode) => {
    // const params = { ...getNormalParams('getUserRoleInfo'), projectCode: projectCode || 'FAP', name };
    const params = { ...getNormalParams('getUserRoleInfo'), projectCode: projectCode || 'yl_tiangong', name };
    const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/user/getUserRoleInfo', {
      params,
    });
    return data;
  },
  addProjectUser: async (projectCode, platformRole, projectRole, userNames) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/user/addUserToProject',
      {
        ...getNormalParams('addUserToProject'),
        projectCode,
        platformRole,
        projectRole,
        userNames,
      },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '添加用户失败');
    } else {
      EeveeMessage.success('成功');
    }
    return data;
  },
  updateProjectUserRole: async (userName, projectRole, platformRole, projectCode) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/user/updateUserProjectRoles',
      { ...getNormalParams('updateProjectUserRole'), userName, projectRole, platformRole, projectCode },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '更新用户角色失败');
    } else {
      EeveeMessage.success('成功');
    }
    return data;
  },
  removeProjectUserRole: async (name, projectCode) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/user/removeProjectUserRole',
      { ...getNormalParams('removeProjectUserRole'), name, projectCode },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '移除用户角色失败');
    } else {
      EeveeMessage.success('成功');
    }
    return data;
  },
  blockUser: async (userName) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>('/api/v1/user/blockUser', {
      ...getNormalParams('blockUser'),
      userName,
    });
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '拉黑失败');
    } else {
      EeveeMessage.success('成功');
    }
    return data;
  },
  unblockUser: async (userName) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/user/unblockUser',
      {
        ...getNormalParams('unblockUser'),
        userName,
      },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '拉黑失败');
    } else {
      EeveeMessage.success('成功');
    }
    return data;
  },
  updateUserAvatar: async ({ avatar }) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/user/updateUserAvatar',
      { ...getNormalParams('updateUserAvatar'), avatar },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '头像更新失败');
    } else {
      EeveeMessage.success('头像更新成功');
    }
    return code;
  },
  getIsNicknameAvailable: async ({ nickName }) => {
    const { code, data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/user/isNicknameAvailable', {
      params: { nickName },
    });
    const isAvailable = data.available;
    if (code === 0) {
      if (!isAvailable) {
        EeveeMessage.error('昵称已存在');
        return false;
      }

      // EeveeMessage.success('昵称可用');
      return true;
    }

    EeveeMessage.error('接口请求失败');
    return false;
  },
  getCurrentUserInfoTiangong: async () => {
    const params = { ...getNormalParams('getCurrentUserInfo') };
    try {
      const response = await FastApiAxiosInstance.get('/api/v1/user/getCurrentUserInfo', { params });

      if (response && response.data) {
        return response.data;
      }
      return null;
    } catch (e) {
      console.error(e);
    }
  },
  async bindPhone(payload: { phone: string; code: string }) {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/bindPhoneNumber`, {
      ...getNormalParams('bindPhoneNumber'),
      ...payload,
    });
    return res;
  },
  // 版权认证-优惠码
  getDiscountCode: async (params: { id: string; type?: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/certify/getDiscountCode`, { params });
    return res;
  },
  // 获取用户信息
  getUserInfo: async (params: { query: string; projectCode?: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/getUserInfo`, {
      params: {
        ...getNormalParams('getUserInfo'),
        ...params,
      },
    });
    return res;
  },
  //批量获取用户信息
  getUserInfoBatchly: (params: { queries: string[]; projectCode?: string }): Promise<ExtendAxioResponse> => {
    return FastApiAxiosInstance.post(`/api/v1/user/getUsersProjectRoleInfo`, params);
  },

  //修改当前登录用户信息
  update: async (payload: {
    nickName?: string;
    avatar?: string;
    personalProfile?: string;
  }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/updateUser`, {
      ...getNormalParams('updateUser'),
      ...payload,
    });

    return res;
  },
  bindNickNameAndAvatar: async (payload: { nickName?: string; avatar: string }): Promise<ExtendAxioResponse> => {
    return await FastApiAxiosInstance.post(`/api/v1/user/bindNickname`, payload);
  },
  //身份证认证
  checkIdCard: async (payload: {
    cardNo: string;
    name: string;
    phone: string;
  }): Promise<ExtendAxioResponse<{ verify: boolean; reasonType: number }>> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/individualCertify`, {
      // type: 0,
      ...payload,
    });

    return res;
  },
  //海外实名认证
  abroadCertification: async (payload: {
    cardType: string;
    cardNo: string;
    birthday: string;
    realName: string;
    address: string;
    cardImgs: IdCardFileType;
  }): Promise<ExtendAxioResponse<{ verify: boolean; reasonType: number }>> => {
    const { cardImgs } = payload;

    // 暂时保留旧的字段拆分逻辑（新的表单已经直接支持输出对应字段，无须拆分）
    cardImgs &&
      Object.entries(cardImgs)?.forEach(([key, file]) => {
        payload[key] = file;
      });

    const res = await FastApiAxiosInstance.post(`/api/v1/user/abroadCertification`, omit(payload, ['address']));

    return res;
  },
  //绑定银行卡
  bindCard: async (payload: { bankCardNo: string; phone: string; bankBranch: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/bindBankCard`, payload);
    return res;
  },

  // 绑定国外银行卡
  bindOverseasCard: async (payload: {
    bankCardNo: string;
    swiftCode: string;
    accountName: string;
  }): Promise<ExtendAxioResponse> => {
    return await FastApiAxiosInstance.post('/api/v1/user/bindBankCard/overseas', payload);
  },

  // 解绑国外银行卡
  unbindOverseasCard: async (): Promise<ExtendAxioResponse> => {
    return await FastApiAxiosInstance.post('/api/v1/user/unbindBankCard/overseas');
  },

  //绑定支付宝
  bindAlipay: async (payload: { alipayNo: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/bindAlipay`, payload);
    return res;
  },
  //解绑支付宝
  unbindAlipay: async ({ code }: { code: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/unbindAlipay`, { code });
    return res;
  },
  unbindBankCard: async ({ code }: { code: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/unbindBankCard`, { code });
    return res;
  },
  //向当前登录手机号发送验证码，不需要传手机号
  sendMyCode: async (code = MobileCode.Unbind): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/sendPhoneCode`, {
      scope: code,
    });
    return res;
  },
  //手机号登录
  login: async (payload: LoginInterface): Promise<ExtendAxioResponse> => {
    return FastApiAxiosInstance.post(`/api/v1/user/phone/login`, payload);
  },
  loginByEmail: async (): Promise<ExtendAxioResponse> => {
    return FastApiAxiosInstance.post(`/api/v1/user/urs/login`);
  },
  //企业认证
  createAuthIdentity: async (params: FormData): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/enterpriseCertify/create`, params);
    return res;
  },
  // 获取开户总行列表
  getBankList: async (): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/enterpriseCertify/bankList`);
    return res;
  },
  //获取开户支行列表
  getSubBankList: async (params: { bankId: string; subBankName?: string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/enterpriseCertify/subBankList`, { params });
    return res;
  },
  userAgreement: async (): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/finishVersionUserAgreement`, {
      params: { ...getNormalParams('userAgreement') },
      withCredentials: true,
    });
    return res;
  },
  selectRole: async (params: { role: UserRoleType }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/selectRole`, { params });
    return res;
  },
  //随机昵称
  randomNickname: async (): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/random/nickName`);
    return res;
  },
  //设置档期
  setSchedule: async (params: { schedules: Schedule[] }): Promise<ExtendAxioResponse> => {
    await FastApiAxiosInstance.post(`/api/v1/user/updateSchedule`, params.schedules);
  },
  getAccountAmountInfo: () => {
    return FastApiAxiosInstance.get<AccountAmountInfo>('/api/v1/user/getAccountInfo');
  },
  //身份证识别
  ocrIdCard(files: any) {
    return FastApiAxiosInstance.post(`/api/v1/user/photo/ocr`, files, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  //手机登录验证码获取
  sendLoginPhoneCode: async (payload: LoginInterface): Promise<ExtendAxioResponse> => {
    return await FastApiAxiosInstance.post(`/api/v1/user/sendLoginPhoneCode`, payload);
  },

  //向指定手机号发送验证码，
  sendCode(payload: LoginInterface & { scope?: MobileCode }) {
    return FastApiAxiosInstance.post(`/api/v1/user/sendPhoneCode`, payload);
  },
  //向指定邮箱发送验证码，
  sendEmailCode(payload: { email?: string; corpEmail?: string; scope: EmailAuthCodeScopeEnum }) {
    return FastApiAxiosInstance.post(`/api/v1/user/sendEmailCode`, payload);
  },

  // 用户认证详情
  getAuthInfo: async (): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.get(`/api/v1/user/getAuthInfo`);
    return res;
  },
  //获取用户支付信息
  getPaymentInfo: async (): Promise<ExtendAxioResponse<PersonalPayInfo>> => {
    return await FastApiAxiosInstance.get(`/api/v1/user/getPaymentInfo`);
    // return new Promise((resolve) => {
    //   resolve({
    //     code: 0,
    //     message: '成功',
    //     data: {
    //       realName: '田一泓',
    //       alipayNo: '18292882256',
    //       bankAccountName: null,
    //       bankCardNo: null,
    //       bankSwiftCode: null,
    //       merchantInfo: {
    //         alipayNo: '18292882256',
    //         status: 'NOT_STARTED',
    //         reason: '',
    //       },
    //     },
    //   });
    // });
  },
  //企业认证打款金额校验
  checkEnterprisePay: async (payload: { amount: string; authId: number | string }): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/enterpriseCertify/validate`, payload);
    return res;
  },
  // 用户阅读平台升级内容
  systemUpdate: async (): Promise<ExtendAxioResponse> => {
    const res = await FastApiAxiosInstance.post(`/api/v1/user/systemUpdate`);
    return res;
  },
  //未登录状态获取平台信息
  getPlatformUserConfig: () => {
    return FastApiAxiosInstance.get<PlatformUserConfig>(`/api/v1/user/getPlatformUserConfig`);
  },
  //获取交互式活体检测二维码
  getIndividualCertifyQrcode: (params: { url: string; cardNo: string; name: string }) => {
    return FastApiAxiosInstance.post<{ qrcode: string }>('/api/v1/user/individualCertify/qrcode/create', params);
  },
  //获取交互式活体检测二维码
  getLiveAuthQrcode: (params: { url: string; cardNo?: string; name?: string; address?: string }) => {
    return FastApiAxiosInstance.post<{ qrcode: string }>('/api/v1/user/liveAuth/qrcode/create', params);
  },
  // 用户状态获取，用于判断是否新用户
  postLoginStatus: () => {
    return FastApiAxiosInstance.get(`/api/v1/user/postLoginStatus`);
  },

  //注销前校验
  logoffValidate: () => {
    return FastApiAxiosInstance.post<CommonListResponse<{ name: string; isPass: boolean; value: string }>>(
      '/api/v1/user/logoff/validate',
    );
  },
  //注销
  logoff: (params: { code: string }) => {
    return FastApiAxiosInstance.post<CommonListResponse<{ name: string; isPass: boolean; value: string }> | undefined>(
      '/api/v1/user/logoff',
      params,
    );
  },
  //绑定企业邮箱
  bindCorpEmail: (params: { corpEmail: string; code: string }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/user/bindCorpEmail', params);
  },
  //绑定邮箱
  bindEmail: (params: { email: string; code: string }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/user/bindEmail', params);
  },
  //校验邮箱是否可用
  validEmail: (params: { email: string }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/user/validEmail', params);
  },
  //校验企业邮箱是否可用
  validCorpEmail: (params: { corpEmail: string }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/user/validCorpEmail', params);
  },
  //校验用户手机验证码
  checkPhoneCode: (code: string) => {
    return FastApiAxiosInstance.post(`/api/v1/user/phone/change/check`, { code });
  },
  //校验用户邮箱验证码
  checkEmailCode: (code: string) => {
    return FastApiAxiosInstance.post(`/api/v1/user/email/change/check`, { code });
  },
  // 校验手机号是否已经绑定
  validateNewPhone: (params: { phone: string; region: string }) => {
    return FastApiAxiosInstance.post(`/api/v1/user/phone/change/valid`, params);
  },
  // 校验邮箱是否已经绑定
  validateNewEmail: (params: { email: string }) => {
    return FastApiAxiosInstance.post(`/api/v1/user/email/change/valid`, params);
  },
  //更改邮箱
  changeEmail: (params: { email: string; code: string }) => {
    return FastApiAxiosInstance.post(`/api/v1/user/email/change`, params);
  },
  //更改手机号
  changePhone: (params: { phone: string; code: string; region: string }) => {
    return FastApiAxiosInstance.post(`/api/v1/user/phone/change`, params);
  },
  //获取平台权限
  getPlatformPermission: () => {
    return FastApiAxiosInstance.post<string[]>(`/api/v1/user/permissions`, undefined);
  },
  //获取单个项目组权限
  getProjectPermission: (params: { projectId?: number; projectCode?: string }) => {
    return FastApiAxiosInstance.post<ProjectsRolePermission>(`/api/v1/user/permissions`, params);
  },
  //获取多个项目组权限
  getProjectsPermission: (params: { projectIds: number[] }) => {
    return FastApiAxiosInstance.post<ProjectsRolePermission>(`/api/v1/user/permissions`, params);
  },
  idAuth(params: { address?: string; cardNo: string; name: string }) {
    return FastApiAxiosInstance.post('/api/v2/user/individualCertify/create', omit(params, ['address']));
  },
  supplementUserInfo: (params: SupplementUserInfoPayload) => {
    return FastApiAxiosInstance.post('/api/v1/user/supplementInfo', params);
  },
};
export default userSetting;
